import * as Phaser from 'phaser';
import { parseLevel } from '../level/loader';
import lvl01 from '../level/data/01-intro.json';
import lvl02 from '../level/data/02-two-mirrors.json';
import lvl03 from '../level/data/03-corner.json';
import lvl04 from '../level/data/04-two-targets.json';
import lvl05 from '../level/data/05-tight.json';
import lvl06 from '../level/data/06-hello-color.json';
import lvl07 from '../level/data/07-pick-a-side.json';
import lvl08 from '../level/data/08-yellow.json';
import lvl09 from '../level/data/09-magenta.json';
import lvl10 from '../level/data/10-spectrum.json';
import { hasSeenHowToPlay, load } from '../progress';
import { LanguageDropdown } from './LanguageDropdown';
import { onLangChange } from '../i18n/helpers';
import { t, isRTL } from '../i18n';
import type { TranslationKey } from '../i18n/types';
import { FONT, PALETTE, TYPE } from '../render/palette';
import { addAtmosphere } from '../render/atmosphere';
import { drawBeamSegment } from '../render/beam';
import { responsiveFontSize } from './responsive-font';

const LEVELS = [lvl01, lvl02, lvl03, lvl04, lvl05, lvl06, lvl07, lvl08, lvl09, lvl10];

export class MenuScene extends Phaser.Scene {
  constructor() {
    super('menu');
  }

  create(): void {
    // First-launch onboarding (unchanged).
    const isE2E =
      typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('e2e');
    if (!isE2E && !hasSeenHowToPlay()) {
      this.scene.start('how-to-play');
      return;
    }

    addAtmosphere(this, 1);

    const progress = load();

    // Title — large, wide letter-spacing, cyan glow.
    const title = this.add
      .text(480, 100, t('meta.gameTitle').toUpperCase(), {
        color: FONT.TITLE,
        fontFamily: TYPE.SANS,
        fontSize: '52px',
        fontStyle: '300',
      })
      .setOrigin(0.5);
    title.setLetterSpacing(22);
    title.setShadow(0, 0, 'rgba(136, 200, 255, 0.55)', 18, true, true);
    onLangChange(this, () => {
      title.setText(t('meta.gameTitle').toUpperCase());
    });

    // Decorative beam beneath the title.
    const banner = this.add.graphics();
    banner.setBlendMode(Phaser.BlendModes.ADD);
    banner.setAlpha(0.5);
    drawBeamSegment(banner, 300, 165, 660, 165, PALETTE.W);

    // Two columns: 5 levels each. Each column is 220px wide with a 48px gap.
    const COL_WIDTH = 220;
    const COL_GAP = 48;
    const COL_LEFT = 480 - COL_WIDTH - COL_GAP / 2; // left column x-origin
    const COL_RIGHT = 480 + COL_GAP / 2; // right column x-origin
    const ROWS_PER_COL = 5;

    LEVELS.forEach((data, i) => {
      const level = parseLevel(data);
      const done = progress[level.id]?.completed ?? false;
      const key = `level.${level.id}.title` as TranslationKey;
      const colIndex = Math.floor(i / ROWS_PER_COL);
      const rowIndex = i % ROWS_PER_COL;
      const rowLeft = colIndex === 0 ? COL_LEFT : COL_RIGHT;
      const rowY = 240 + rowIndex * 40;
      const row = this.buildLevelRow(rowLeft, COL_WIDTH, rowY, i + 1, done, key);
      row.hit.on('pointerdown', () => {
        this.scene.start('puzzle', { level });
      });
    });

    const iconY = 240 + ROWS_PER_COL * 40 + 40;
    const iconGap = 56;

    const help = this.makeHelpButton(0, iconY);
    help.on('pointerdown', () => this.scene.start('how-to-play'));

    const cog = this.makeCogButton(0, iconY);
    cog.on('pointerdown', () => this.scene.start('options'));

    const placeIcons = (): void => {
      if (isRTL()) {
        help.x = 480 + iconGap / 2;
        cog.x = 480 - iconGap / 2;
      } else {
        help.x = 480 - iconGap / 2;
        cog.x = 480 + iconGap / 2;
      }
    };
    placeIcons();
    onLangChange(this, placeIcons);

    new LanguageDropdown(this);
  }

  /** "?" icon inside a circular glass ring. */
  private makeHelpButton(x: number, y: number): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    const ring = this.add.circle(0, 0, 19, PALETTE.SLOT_FILL, 0.4);
    ring.setStrokeStyle(1, PALETTE.HUD_TITLE, 0.12);
    const fs = responsiveFontSize(this, 16);
    const glyph = this.add
      .text(0, 1, '?', {
        color: FONT.TEXT,
        fontFamily: TYPE.SANS,
        fontSize: `${fs}px`,
        fontStyle: '500',
      })
      .setOrigin(0.5);
    container.add([ring, glyph]);
    const hitSide = this.sys.game.device.input.touch ? 44 : 38;
    const hitHalf = hitSide / 2;
    container.setSize(hitSide, hitSide);
    container.setInteractive(
      new Phaser.Geom.Rectangle(-hitHalf, -hitHalf, hitSide, hitSide),
      (r: Phaser.Geom.Rectangle, px: number, py: number) =>
        Phaser.Geom.Rectangle.Contains(r, px, py),
    );
    container.on('pointerover', () => {
      ring.setStrokeStyle(1, PALETTE.HUD_TITLE, 0.4);
      glyph.setColor(FONT.TITLE);
    });
    container.on('pointerout', () => {
      ring.setStrokeStyle(1, PALETTE.HUD_TITLE, 0.12);
      glyph.setColor(FONT.TEXT);
    });
    return container;
  }

  /** Cog icon inside a circular glass ring. */
  private makeCogButton(x: number, y: number): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    const ring = this.add.circle(0, 0, 19, PALETTE.SLOT_FILL, 0.4);
    ring.setStrokeStyle(1, PALETTE.HUD_TITLE, 0.12);
    const glyph = this.add.graphics();
    drawCog(glyph, 0xdddddd);
    container.add([ring, glyph]);
    const hitSide = this.sys.game.device.input.touch ? 44 : 38;
    const hitHalf = hitSide / 2;
    container.setSize(hitSide, hitSide);
    container.setInteractive(
      new Phaser.Geom.Rectangle(-hitHalf, -hitHalf, hitSide, hitSide),
      (r: Phaser.Geom.Rectangle, px: number, py: number) =>
        Phaser.Geom.Rectangle.Contains(r, px, py),
    );
    container.on('pointerover', () => {
      ring.setStrokeStyle(1, PALETTE.HUD_TITLE, 0.4);
      drawCog(glyph, PALETTE.HUD_TITLE);
    });
    container.on('pointerout', () => {
      ring.setStrokeStyle(1, PALETTE.HUD_TITLE, 0.12);
      drawCog(glyph, 0xdddddd);
    });
    return container;
  }

  /**
   * Build a single level row — check glyph, mono-styled 2-digit index, and
   * the level title in sans. The visual elements live inside a Container so
   * they can slide together on hover. The hit target stays static and is
   * returned for the caller to wire clicks.
   */
  private buildLevelRow(
    rowLeft: number,
    rowWidth: number,
    y: number,
    index: number,
    done: boolean,
    key: TranslationKey,
  ): { hit: Phaser.GameObjects.Rectangle } {
    const baseX = rowLeft + 12; // padding inside the column
    const container = this.add.container(baseX, y);

    const fsCheck = responsiveFontSize(this, 14);
    const check = this.add
      .text(0, 0, done ? '✓' : '·', {
        color: done ? FONT.GREEN : FONT.DIM,
        fontFamily: TYPE.SANS,
        fontSize: `${fsCheck}px`,
        fontStyle: '500',
      })
      .setOrigin(0.5);

    const fsNum = responsiveFontSize(this, 13);
    const num = this.add
      .text(20, 0, String(index).padStart(2, '0'), {
        color: FONT.DIM,
        fontFamily: TYPE.MONO,
        fontSize: `${fsNum}px`,
      })
      .setOrigin(0, 0.5);

    const fsTitle = responsiveFontSize(this, 16);
    const titleText = this.add
      .text(54, 0, t(key), {
        color: FONT.TEXT,
        fontFamily: TYPE.SANS,
        fontSize: `${fsTitle}px`,
      })
      .setOrigin(0, 0.5);

    container.add([check, num, titleText]);

    // Hit zone covers the full column width so clicks anywhere on the row land.
    // On touch ensure at least 44px height for accessible tap targets.
    const hitH = this.sys.game.device.input.touch ? Math.max(32, 44) : 32;
    const hit = this.add
      .rectangle(rowLeft + rowWidth / 2, y, rowWidth, hitH, 0xffffff, 0)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    hit.on('pointerover', () => {
      titleText.setColor(FONT.TITLE);
      this.tweens.add({
        targets: container,
        x: baseX + 4,
        duration: 180,
        ease: 'Sine.easeOut',
      });
    });
    hit.on('pointerout', () => {
      titleText.setColor(FONT.TEXT);
      this.tweens.add({
        targets: container,
        x: baseX,
        duration: 180,
        ease: 'Sine.easeOut',
      });
    });

    const apply = (): void => {
      titleText.setText(t(key));
    };
    onLangChange(this, apply);

    return { hit };
  }
}

function drawCog(gfx: Phaser.GameObjects.Graphics, color: number): void {
  gfx.clear();
  gfx.lineStyle(1.4, color, 1);
  gfx.strokeCircle(0, 0, 2.5);
  for (let i = 0; i < 8; i++) {
    const a = (i * Math.PI) / 4;
    const r1 = 4.5;
    const r2 = 6.5;
    gfx.lineBetween(Math.cos(a) * r1, Math.sin(a) * r1, Math.cos(a) * r2, Math.sin(a) * r2);
  }
}
